import { WithAuthorization } from "../../../context";
import { Page } from "../../../components/Page";
import { useTranslation } from "react-i18next";
import { useCallback, useMemo, useRef, useState } from "react";
import {
  AlertDialog, AlertDialogBody, AlertDialogContent, AlertDialogFooter, AlertDialogHeader, AlertDialogOverlay,
  Button, Flex, HStack, Select, Stack, Tab, TabList, TabPanel, TabPanels, Tabs, Text, useDisclosure, useToast,
} from "@chakra-ui/react";
import { ArrowRight, PencilSimple } from "@phosphor-icons/react";
import { BespokeFactorsTab, ScopeOneFactorsTab, ScopeThreeFactorsTab, ScopeTwoFactorsTab } from "./components";
import { useGetOrganizationStructure } from "../../../hooks/query";
import { useGetEmissionFactorMapping, useUpdateEmissionFactorMapping, organizationAdministrationKeys } from "../../../hooks/query/organization-administration";
import { useQueryClient } from "react-query";
import { generateArrayOfYears, showSuccessToast } from "../../../utils";
import PageHeader from "../../../components/text/PageHeader";

function ConversionFactors() {
  const { t } = useTranslation("translation", {
    keyPrefix: "conversionFactors"
  });
  const toast = useToast();
  const queryClient = useQueryClient();
  const cancelRef = useRef();

  const [tabIndex, setTabIndex] = useState(0);
  const [selectedFinancialYear, setSelectedFinancialYear] = useState((new Date().getFullYear() - 1).toString());
  const [isEditingMapping, setIsEditingMapping] = useState(false);
  const [pendingFactorYear, setPendingFactorYear] = useState(null);
  const { isOpen: isConfirmOpen, onOpen: onConfirmOpen, onClose: onConfirmClose } = useDisclosure();

  const { data: organizationStructure } = useGetOrganizationStructure();
  const { data: emissionFactorMapping } = useGetEmissionFactorMapping();
  const { mutate: updateMapping } = useUpdateEmissionFactorMapping({
    onSuccess: () => {
      queryClient.invalidateQueries(organizationAdministrationKeys.emissionFactorMapping());
      showSuccessToast(toast, "Emission factor year mapping updated");
      setIsEditingMapping(false);
      setPendingFactorYear(null);
    },
  });

  const factorYear = useMemo(() => {
    if (emissionFactorMapping && emissionFactorMapping[selectedFinancialYear]) {
      return emissionFactorMapping[selectedFinancialYear];
    }
    return selectedFinancialYear;
  }, [emissionFactorMapping, selectedFinancialYear]);

  const handleFactorYearSelect = (newFactorYear) => {
    if (newFactorYear === factorYear) return;
    setPendingFactorYear(newFactorYear);
    onConfirmOpen();
  };

  const handleConfirmMapping = () => {
    const updated = { ...(emissionFactorMapping || {}), [selectedFinancialYear]: pendingFactorYear };
    if (pendingFactorYear === selectedFinancialYear) {
      delete updated[selectedFinancialYear];
    }
    updateMapping(updated);
    onConfirmClose();
  };

  const handleCancelMapping = useCallback(() => {
    onConfirmClose();
    setPendingFactorYear(null);
    setIsEditingMapping(false);
  }, [onConfirmClose]);

  const years = useMemo(() => generateArrayOfYears(), []);

  const pillSelectStyles = {
    borderRadius: "full",
    border: "1px solid",
    borderColor: "gray.200",
    bg: "white",
    fontSize: "body.s",
    fontWeight: "500",
    color: "gray.700",
    h: "36px",
    cursor: "pointer",
    _hover: { borderColor: "gray.400" },
    _focus: { borderColor: "lime", boxShadow: "0 0 0 1px var(--chakra-colors-lime)" },
  };

  return (
    <Page>
      <Stack spacing={10} p={4} bg="pureWhite" marginLeft="15%" marginRight="15%">
        <PageHeader title={t("page.title")} subTitle={t("page.subTitle")} />

        <Flex gap={3} align="center" wrap="wrap">
          <Select {...pillSelectStyles} w="auto" minW="180px" value={selectedFinancialYear} onChange={(e) => { setSelectedFinancialYear(e.target.value); setIsEditingMapping(false); }}>
            {years.map((y) => <option key={y} value={y}>Financial Year {y}</option>)}
          </Select>
          <HStack spacing={1.5} bg="lightLime" border="1px solid" borderColor="lime" borderRadius="full" px={3} h="36px">
            <Text fontSize="body.s" fontWeight="500" color="limeShade.700" whiteSpace="nowrap">uses</Text>
            <ArrowRight size={14} color="var(--chakra-colors-limeShade-700)" />
          </HStack>
          {isEditingMapping ? (
            <HStack spacing={2}>
              <Select
                {...pillSelectStyles}
                w="auto"
                minW="170px"
                borderColor="lime"
                bg="lightLime"
                value={factorYear}
                onChange={(e) => handleFactorYearSelect(e.target.value)}
              >
                {years.map((y) => <option key={y} value={y}>Factor Year {y}</option>)}
              </Select>
              <Button size="sm" variant="ghost" borderRadius="full" color="gray.500" onClick={() => setIsEditingMapping(false)}>
                Cancel
              </Button>
            </HStack>
          ) : (
            <HStack spacing={2}>
              <Text
                fontSize="body.s"
                fontWeight="600"
                color={factorYear !== selectedFinancialYear ? "limeShade.700" : "gray.700"}
                bg={factorYear !== selectedFinancialYear ? "lightLime" : "gray.50"}
                border="1px solid"
                borderColor={factorYear !== selectedFinancialYear ? "lime" : "gray.200"}
                borderRadius="full"
                px={4}
                h="36px"
                display="flex"
                alignItems="center"
                whiteSpace="nowrap"
              >
                Factor Year {factorYear}
              </Text>
              <Button
                size="sm"
                variant="ghost"
                borderRadius="full"
                color="gray.500"
                leftIcon={<PencilSimple size={14} />}
                _hover={{ color: "gray.700", bg: "gray.100" }}
                onClick={() => setIsEditingMapping(true)}
              >
                Change
              </Button>
            </HStack>
          )}
        </Flex>

        <AlertDialog isOpen={isConfirmOpen} leastDestructiveRef={cancelRef} onClose={handleCancelMapping}>
          <AlertDialogOverlay>
            <AlertDialogContent>
              <AlertDialogHeader fontSize="lg" fontWeight="bold">Change Factor Year Mapping</AlertDialogHeader>
              <AlertDialogBody>
                This will change the emission factors used for Financial Year {selectedFinancialYear} across the platform.
                All calculations for this year will use <strong>Factor Year {pendingFactorYear}</strong> instead of <strong>Factor Year {factorYear}</strong>.
              </AlertDialogBody>
              <AlertDialogFooter>
                <Button ref={cancelRef} onClick={handleCancelMapping}>Cancel</Button>
                <Button variant="primary" onClick={handleConfirmMapping} ml={3}>Confirm</Button>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialogOverlay>
        </AlertDialog>

        <Tabs variant="primary" index={tabIndex} onChange={setTabIndex} isLazy>
          <TabList>
            <Tab>{t("tabs.scope")} 1</Tab>
            <Tab>{t("tabs.scope")} 2</Tab>
            <Tab>{t("tabs.scope")} 3</Tab>
            <Tab>{t("tabs.bespoke")}</Tab>
          </TabList>
          {/* The factor endpoints apply the org year mapping themselves, so the data tabs get the
              financial year — passing factorYear mapped it twice (ESG-4017). Bespoke keeps factorYear:
              its localStorage overrides are keyed by it. */}
          <TabPanels>
            <TabPanel px={0}>
              <ScopeOneFactorsTab selectedYear={selectedFinancialYear} setSelectedYear={() => {}} organizationStructure={organizationStructure} />
            </TabPanel>
            <TabPanel px={0}>
              <ScopeTwoFactorsTab selectedYear={selectedFinancialYear} setSelectedYear={() => {}} organizationStructure={organizationStructure} />
            </TabPanel>
            <TabPanel px={0}>
              <ScopeThreeFactorsTab selectedYear={selectedFinancialYear} organizationStructure={organizationStructure} />
            </TabPanel>
            <TabPanel px={0}>
              <BespokeFactorsTab selectedYear={factorYear} organizationStructure={organizationStructure} />
            </TabPanel>
          </TabPanels>
        </Tabs>
      </Stack>
    </Page>
  );
}

export default WithAuthorization(ConversionFactors);